import React, { useEffect, useMemo } from 'react';

import { useGetDepartments, useGetSelf } from '@vlabs/api/artemy/client';
import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';
import validate from '@vlabs/shared/validators';
import type {
  Control, FieldErrors, FieldValues, Path, PathValue, UseFormSetValue,
} from 'react-hook-form';

interface OwnerDepartmentSelectProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  label?: string;
  setValue: UseFormSetValue<TFieldValues>;
}

export function OwnerDepartmentSelect<TFieldValues extends FieldValues = FieldValues>({
  control,
  errors,
  label = undefined,
  setValue,
}: OwnerDepartmentSelectProps<TFieldValues>): React.JSX.Element {
  const { data: self, isLoading: selfIsLoading } = useGetSelf();

  const departmentIds = useMemo(() => self?.department_ids ?? [], [self?.department_ids]);
  const { data: departmentsData, isLoading: departmentsIsLoading } = useGetDepartments(
    { department_ids: departmentIds.join(',') },
    { query: { enabled: departmentIds.length > 0 } },
  );

  const options = (departmentsData?.departments ?? []).map(({ department_id, name }) => ({
    label: name ?? department_id,
    value: department_id,
  }));
  const fieldName = 'owner_department_id' as Path<TFieldValues>;

  useEffect(() => {
    if (departmentIds.length !== 1) return;

    setValue(
      fieldName,
      departmentIds[0] as PathValue<TFieldValues, Path<TFieldValues>>,
      { shouldValidate: true },
    );
  }, [departmentIds, fieldName, setValue]);

  return (
    <ControllableValueSelect
      control={control}
      disabled={selfIsLoading || departmentsIsLoading || departmentIds.length === 0}
      errors={errors}
      id={fieldName}
      isClearable={departmentIds.length > 1}
      label={label}
      name={fieldName}
      options={options}
      rules={{ required: validate.required() }}
    />
  );
}
